import { ReactNode } from 'react';
import { Banner, Button, Divider, Icon, IconName, Stack, Tag, Typography } from '@optimus-web/core';
import { lt } from '@mews/framework';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { DigitalKeyConfigurationStatus } from '@mews-commander/data-access';
import { EnterpriseLink, useEnterpriseNavigate } from '@mews-commander/ui';
import { useGetDigitalKeyConfigurationStatus } from '../apiHooks';

interface ConfigurationItemProps {
    icon: IconName;
    label: ReactNode;
    description: ReactNode;
    status?: DigitalKeyConfigurationStatus;
    onClick: () => void;
}

const ConfigurationItem = ({ icon, label, description, status, onClick }: ConfigurationItemProps) => (
    <Stack spacing="400" itemAlignment="center" distribution="space-between">
        <Stack spacing="200" itemAlignment="center">
            <Icon value={icon} />
            <Stack vertical>
                <Typography textStyle="bodyLargeStrong" as="div">
                    {label}
                </Typography>
                <Typography textStyle="bodyMedium" as="div">
                    {description}
                </Typography>
            </Stack>
        </Stack>

        <Stack>
            {status === DigitalKeyConfigurationStatus.Configured ? (
                <Tag variant="success" leadingIcon={IconName.CheckCircle}>
                    {lt('Complete')}
                </Tag>
            ) : (
                <Button size="small" type="button" variant="tertiary" onClick={onClick}>
                    {lt('Configure')}
                </Button>
            )}
        </Stack>
    </Stack>
);

export const DigitalKeyConfigurationOverviewContainer = () => {
    const navigate = useEnterpriseNavigate();
    const enterpriseId = useCurrentEnterpriseId();
    const { data: configuration, isLoading } = useGetDigitalKeyConfigurationStatus();

    const hideConfiguration =
        configuration?.signInImageConfigurationStatus === DigitalKeyConfigurationStatus.Configured &&
        configuration?.spaceImagesConfigurationStatus === DigitalKeyConfigurationStatus.Configured &&
        configuration?.spaceDirectionsConfigurationStatus === DigitalKeyConfigurationStatus.Configured;

    return (
        <Stack spacing="300" vertical>
            {!configuration?.isActivated && !isLoading && (
                <Banner
                    variant="error"
                    primaryText={lt('DigitalKeyNotActivatedBanner')}
                    secondaryText={lt('DigitalKeyNotActivatedBannerDescription')}
                    action={
                        <EnterpriseLink to="Service/Index" variant="basic" search={{ OwnerId: enterpriseId }}>
                            {lt('GoToBookableServices')}
                        </EnterpriseLink>
                    }
                />
            )}
            {!hideConfiguration && (
                <Stack vertical spacing="300">
                    <Typography textStyle="titleLargeStrong" as="div">
                        {lt('DigitalKeyConfigurationOverViewLabel')}
                    </Typography>
                    <Divider />
                    <ConfigurationItem
                        icon={IconName.Companies}
                        label={lt('DigitalKeyAddSignInImage')}
                        description={lt('DigitalKeyAddSignInImageDescription')}
                        status={configuration?.signInImageConfigurationStatus}
                        onClick={() =>
                            void navigate({ to: 'Enterprise/Detail/$enterpriseId', params: { enterpriseId } })
                        }
                    />
                    <Divider />
                    <ConfigurationItem
                        icon={IconName.DoubleBed}
                        label={lt('DigitalKeyAddImageToYourSpaces')}
                        description={lt('DigitalKeyAddImageToYourSpaceDescription')}
                        status={configuration?.spaceImagesConfigurationStatus}
                        onClick={() => void navigate({ to: 'Service/Index', search: { OwnerId: enterpriseId } })}
                    />
                    <Divider />
                    <ConfigurationItem
                        icon={IconName.Location}
                        label={lt('DigitalKeyAddDirectionsToYourSpaces')}
                        description={lt('DigitalKeyAddDirectionsToYourSpaceDescription')}
                        status={configuration?.spaceDirectionsConfigurationStatus}
                        onClick={() => void navigate({ to: 'Resource/Index', search: { OwnerId: enterpriseId } })}
                    />
                    <Divider />
                </Stack>
            )}
        </Stack>
    );
};
